import { sumCohortRowFromMonth } from './twelvePerspective';

export function parseRunoffExposureCohortCount(value) {
  const count = Number(value);
  if (!Number.isFinite(count) || count <= 0) {
    return null;
  }
  return Math.floor(count);
}

function planCohortHasActivity({
  cohortIndex,
  outputMonth,
  cohortRevenue,
  marketing,
  cohortDraws,
}) {
  if (cohortIndex > outputMonth) {
    return false;
  }
  if ((Number(marketing?.[cohortIndex]) || 0) > 0) {
    return true;
  }
  if ((Number(cohortDraws?.[cohortIndex]) || 0) > 0) {
    return true;
  }
  const revenueRow = cohortRevenue?.[cohortIndex];
  if (!revenueRow) {
    return false;
  }
  for (let month = 0; month <= outputMonth; month += 1) {
    if ((Number(revenueRow[month]) || 0) > 0) {
      return true;
    }
  }
  return false;
}

function oldCohortRowIsActive(row, outputMonth) {
  const cohortStart = row?.cohortStart;
  if (cohortStart == null || !Number.isFinite(cohortStart) || cohortStart > outputMonth) {
    return false;
  }
  const amounts = row?.amounts ?? [];
  for (let month = 0; month <= outputMonth; month += 1) {
    if ((Number(amounts[month]) || 0) > 0) {
      return true;
    }
  }
  return false;
}

/**
 * Pick plan cohort indices and old-cohort sheet rows whose remaining revenue
 * defines runoff at `outputMonth`.
 */
export function selectRunoffCohortSources({
  outputMonth,
  cohortRevenue,
  cohortDraws,
  marketing,
  oldCohortRevenueRows = [],
  cohortCount,
}) {
  if (!cohortCount || cohortCount <= 0) {
    return { planIndices: [], oldRowIndices: [] };
  }

  const eligiblePlan = [];
  const planLength = Math.max(
    cohortRevenue?.length ?? 0,
    marketing?.length ?? 0,
    cohortDraws?.length ?? 0
  );
  for (let cohortIndex = 0; cohortIndex <= outputMonth && cohortIndex < planLength; cohortIndex += 1) {
    if (
      planCohortHasActivity({
        cohortIndex,
        outputMonth,
        cohortRevenue,
        marketing,
        cohortDraws,
      })
    ) {
      eligiblePlan.push(cohortIndex);
    }
  }

  const fundedPlan = eligiblePlan.filter(
    (cohortIndex) => (Number(cohortDraws?.[cohortIndex]) || 0) > 0
  );
  const unfundedPlan = eligiblePlan.filter(
    (cohortIndex) => (Number(cohortDraws?.[cohortIndex]) || 0) <= 0
  );

  const eligibleOld = (oldCohortRevenueRows ?? [])
    .map((row, rowIndex) => ({ row, rowIndex }))
    .filter(({ row }) => oldCohortRowIsActive(row, outputMonth));

  if (fundedPlan.length >= cohortCount) {
    return {
      planIndices: fundedPlan.slice().sort((a, b) => a - b),
      oldRowIndices: [],
    };
  }

  const unfundedPlanMostRecentFirst = unfundedPlan.slice().sort((a, b) => b - a);
  const oldRowsMostRecentFirst = eligibleOld
    .slice()
    .sort((a, b) => b.row.cohortStart - a.row.cohortStart);

  let remaining = cohortCount - fundedPlan.length;
  const paddedPlan = unfundedPlanMostRecentFirst.slice(0, remaining);
  remaining -= paddedPlan.length;

  const paddedOld = oldRowsMostRecentFirst
    .slice(0, remaining)
    .map(({ rowIndex }) => rowIndex);

  return {
    planIndices: fundedPlan.concat(paddedPlan).sort((a, b) => a - b),
    oldRowIndices: paddedOld.sort((a, b) => a - b),
  };
}

/** @deprecated Use selectRunoffCohortSources — kept for tests expecting plan indices only. */
export function selectRunoffCohortIndices(args) {
  return selectRunoffCohortSources(args).planIndices;
}

function sumSelectedRunoffAtMonth({
  outputMonth,
  planIndices,
  oldRowIndices,
  cohortRevenue,
  oldCohortRevenueRows,
  N_OUT,
}) {
  let total = 0;
  for (const cohortIndex of planIndices) {
    total += sumCohortRowFromMonth(cohortRevenue?.[cohortIndex] ?? [], outputMonth, N_OUT);
  }
  for (const rowIndex of oldRowIndices) {
    total += sumCohortRowFromMonth(
      oldCohortRevenueRows?.[rowIndex]?.amounts ?? [],
      outputMonth,
      N_OUT
    );
  }
  return total;
}

export function calcRunoffByFundedCohortWindow({
  cohortRevenue,
  cohortDraws,
  marketing,
  oldCohortRevenueRows = [],
  cohortCount,
  N_OUT,
}) {
  const runoff = new Array(N_OUT).fill(0);
  const parsedCount = parseRunoffExposureCohortCount(cohortCount);
  if (parsedCount == null) {
    return runoff;
  }

  for (let outputMonth = 0; outputMonth < N_OUT; outputMonth += 1) {
    const sources = selectRunoffCohortSources({
      outputMonth,
      cohortRevenue,
      cohortDraws,
      marketing,
      oldCohortRevenueRows,
      cohortCount: parsedCount,
    });
    runoff[outputMonth] = sumSelectedRunoffAtMonth({
      outputMonth,
      planIndices: sources.planIndices,
      oldRowIndices: sources.oldRowIndices,
      cohortRevenue,
      oldCohortRevenueRows,
      N_OUT,
    });
  }

  return runoff;
}
